/**
 * black_hole_look — the sector's black hole: the engine lens fed with the SPACE SKY (shader gaps K, 2026-09-30).
 *
 * The engine's BlackHoleLens (enginex/render/space/blackhole — Bruneton 2020, the tables, the disc) knows nothing of
 * Atlas; this file hands it the sector's own sky as TSL builders along a bent direction:
 *   - EXTENDED: the starless Milky Way photo (space_sky's texture, gain SPACE_BG_GAIN) at the equirect uv of the bent
 *     direction, sampled with ANALYTIC gradients from the direction's screen derivatives (continuous across the atan2
 *     seam — the lensed image of that seam would otherwise be a curve of the coarsest mip);
 *   - POINTS: the catalogue's vacuum star field (space_stars.buildSpaceStarField — its own footprint filter reads
 *     fwidth(dir), which along the bent direction IS the lensed beam: magnified stars stay round, minified ones dim);
 *   - POINTS MEAN: the stars' average radiance (black_hole.starFieldMeanRadiance) for beams wider than the filter.
 * The sky is rotated per system exactly as the background is (space_look.systemSkyQuat), so a hole far away changes
 * nothing and the lensed sky meets the plain one without a seam.
 *
 * `BlackHoleController` is the placement + per-frame glue SpaceModule installs (`window.__blackHole`): one hole per
 * sector entry, absolute position (FP64) → render position each frame, the flag read LIVE (off / VR → hidden and the
 * plain background restored; a quality change rebuilds at the new table size), the body compiled with compileAsync
 * before its first draw (THE WARM).
 *
 * THE SUN IS A SOURCE (space look, 2026-10-08). The sector's stars are quads (lib/space/space_sun.ts) the lens could
 * not bend: its body cap hid them — "a black area around the black hole". `spaceLensSky(assets, bodies)` hands the lens
 * each star body as a SOURCE (the quads' own disc and corona functions along the pixel's escaping ray), and while the
 * lens is SHOWN the controller owns those bodies: their disc and corona quads are hidden (drawn once, by the lens, in
 * its sky pass and its cap alike — no seam), each star's place relative to the hole is written every frame
 * (black_hole.starLensSeat — beyond the hole it is bent, in front of it it is drawn over the hole un-bent), and the
 * glare quad is moved onto the star's IMAGE. Released the frame the lens is hidden or removed.
 */
import * as THREE from 'three/webgpu';
import { uniform, vec2, vec3, vec4, float, normalize, atan, asin, clamp, sqrt, max, log, length, fwidth, texture as tslTexture } from 'three/tsl';
import { BlackHoleLens, type BlackHoleSky, type BlackHoleRay } from 'enginex/render/space/blackhole/BlackHoleLens.js';
import { bhImageAngle, bhDepthSafeDistance, BH_BODY_DEPTH_CODES } from 'enginex/render/space/blackhole/SchwarzschildMath.js';
import { buildSpaceStarField } from './space_stars.js';
import { starLensSource, setBodyLensOwned, writeBodyLens, seatBodyGlare, type StarBody } from './space_sun.js';
import { catalogPeaks, type SpaceSkyAssets } from './space_sky.js';
import { quantile, SPACE_BG_GAIN, SPACE_STAR_GAMMA, SPACE_STAR_GAMMA_HI, SPACE_STAR_P99_TARGET, systemSkyQuat } from './space_look.js';
import { STAR_SHRINK, STAR_HALO, starReach, type StarCatalog } from '../sky/star_catalog.js';
import { buildLensStarTables, lensWideMean, centreFootprint, LENS_F_MIN, LENS_F_MAX, LENS_LUT_N, LENS_DENS_W, LENS_DENS_H, LENS_FLOOR_SHARE, type LensStarTables } from './lens_stars.js';
import {
  schwarzschildRadiusM, resolveDisc, placementAhead, renderPosition, normalizeAt, starFieldMeanRadiance, starLensSeat, BACK_SEAT_CODES, BACK_SEAT_KEY, CAP_FORWARD_CODES, FRONT_SEAT_M,
  BLACK_HOLE_DEFAULT_MASS, type BlackHolePlaceInput, type BlackHoleQualityFlag, type V3, type ResolvedDisc, type StarLensSeat,
} from './black_hole.js';
import { disposeAfterSwap } from '../gpu_dispose.js';

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any

/** The representative pixel floor (chord / pixel ≈ 1080p at the chase FOV) — space_sky's GAIN_PX_FLOOR. */
const PX_FLOOR = 0.001;

/**
 * The space sky as a BlackHoleSky (the textures and the star records are the background's own). `bodies` are the star
 * bodies the lens draws as SOURCES (space_sun.starLensSource) — fixed for this kit: a different set is a different shader.
 */
/** The lens's star tables, once per catalogue (lens_stars: ~40 ms of sums over the catalogue). */
const lensTables = new WeakMap<StarCatalog, LensStarTables>();
/** `localStorage atlas:bh-stars = 'legacy'`: the stars as the lens drew them before 2026-10-08 (dimmed by the lensed footprint) — the A/B. */
export function lensStarsMode(): 'conserve' | 'legacy' {
  try { return localStorage.getItem('atlas:bh-stars') === 'legacy' ? 'legacy' : 'conserve'; } catch { return 'conserve'; }
}
const half = (a: Float32Array, n: number, stride: number, ch: number): Uint16Array => {
  const out = new Uint16Array(n * 4);
  for (let i = 0; i < n; i++) for (let c = 0; c < 4; c++) out[i * 4 + c] = THREE.DataUtils.toHalfFloat(c < ch ? Math.min(60000, a[i * stride + c]) : 1);
  return out;
};
export interface SpaceLensKit {
  sky: BlackHoleSky; setSystem(id: string): void; state: { p99: number; mean: number }; bodies: readonly StarBody[];
  /** 'conserve' = the stars keep their light through the lens (lens_stars); 'legacy' = the lensed footprint dims them. */
  stars: 'conserve' | 'legacy';
  tables: LensStarTables | null;
  /** The plain sky's mean at this display — what the lens puts where a beam spans more sky than any filter resolves. */
  wideMean(fovYRad: number, heightPx: number): number;
  /** Beds: this pass draws `k`× finer than the display it stands for (a 4× truth keeps the 1× display's star floor). */
  setFloorScale(k: number): void;
  dispose(): void;
}
export function spaceLensSky(assets: SpaceSkyAssets, bodies: readonly StarBody[] = [], opts: { stars?: 'conserve' | 'legacy' } = {}): SpaceLensKit {
  const uRot = uniform(new THREE.Matrix3());
  const uFloorScale = uniform(1);
  const mode = opts.stars ?? lensStarsMode();
  let tables: LensStarTables | null = null;
  let lutTex: THREE.DataTexture | null = null, densTex: THREE.DataTexture | null = null;
  const uBg = uniform(SPACE_BG_GAIN);
  const uInvP99 = uniform(1), uGamma = uniform(SPACE_STAR_GAMMA), uTarget = uniform(SPACE_STAR_P99_TARGET);
  const state = { p99: 0, mean: 0 };
  const cat = assets.catalog;
  if (cat) {
    const peaks = catalogPeaks(cat, PX_FLOOR);
    state.p99 = quantile(peaks, 0.99);
    (uInvP99 as unknown as { value: number }).value = state.p99 > 0 ? 1 / state.p99 : 1;
    state.mean = starFieldMeanRadiance(cat.sigma, cat.flux, state.p99, { shrink: STAR_SHRINK, pxFloor: PX_FLOOR * 0.75, gamma: SPACE_STAR_GAMMA, gammaHi: SPACE_STAR_GAMMA_HI, target: SPACE_STAR_P99_TARGET });
    if (mode === 'conserve' && assets.stars && state.p99 > 0) {
      tables = lensTables.get(cat) ?? null;
      if (!tables) { tables = buildLensStarTables(cat, { p99: state.p99, shrink: STAR_SHRINK, gamma: SPACE_STAR_GAMMA, gammaHi: SPACE_STAR_GAMMA_HI, target: SPACE_STAR_P99_TARGET, halo: STAR_HALO, reachOf: starReach }); lensTables.set(cat, tables); }
      const mk = (data: Uint16Array, w: number, h: number, wrapS: THREE.Wrapping): THREE.DataTexture => {
        const t = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.HalfFloatType);
        t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter; t.generateMipmaps = false; t.flipY = false;
        t.wrapS = wrapS; t.wrapT = THREE.ClampToEdgeWrapping; t.colorSpace = THREE.NoColorSpace; t.needsUpdate = true;
        return t;
      };
      lutTex = mk(half(tables.lut, LENS_LUT_N, 2, 2), LENS_LUT_N, 1, THREE.ClampToEdgeWrapping);
      densTex = mk(half(tables.dens, LENS_DENS_W * LENS_DENS_H, 3, 3), LENS_DENS_W, LENS_DENS_H, THREE.RepeatWrapping);
    }
  }
  const setSystem = (id: string): void => {
    const q = systemSkyQuat(id);
    (uRot as unknown as { value: THREE.Matrix3 }).value.setFromMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(new THREE.Quaternion(q[0], q[1], q[2], q[3])));
  };
  const R = uRot as N;
  const sky: BlackHoleSky = {
    extended: assets.bg ? (dir: N, ddx: N, ddy: N): N => {
      const d = normalize(R.mul(dir)), dx = R.mul(ddx), dy = R.mul(ddy);
      // three's equirectUV: u = atan2(z, x)/2π + ½, v = asin(y)/π + ½ — and its derivatives, continuous at the seam
      const uvv = vec2(atan(d.z, d.x).mul(1 / (2 * Math.PI)).add(0.5), asin(clamp(d.y, -1, 1)).mul(1 / Math.PI).add(0.5));
      const rxz = max(d.x.mul(d.x).add(d.z.mul(d.z)), 1e-8);
      const cy = sqrt(max(d.y.mul(d.y).oneMinus(), 1e-6)).mul(Math.PI);
      const gx = vec2(d.x.mul(dx.z).sub(d.z.mul(dx.x)).div(rxz.mul(2 * Math.PI)), dx.y.div(cy));
      const gy = vec2(d.x.mul(dy.z).sub(d.z.mul(dy.x)).div(rxz.mul(2 * Math.PI)), dy.y.div(cy));
      return (tslTexture(assets.bg as THREE.Texture, uvv) as N).grad(gx, gy).rgb.mul(uBg);
    } : undefined,
    points: assets.stars ? (dir: N, view?: N): N => {
      const plain = { invP99: uInvP99, gamma: uGamma, target: uTarget };
      // 'legacy', or a lens that hands no view ray (the frozen 2026-09-30 copy): the stars as they were
      if (!tables || !lutTex || !densTex || view === undefined) return buildSpaceStarField(normalize(R.mul(dir)), assets.stars!, plain);
      // THE DISPLAY'S STRETCH, THE LENS'S SHAPE + WHAT THE WINDOW CUTS COMES BACK AS THE MEAN (lens_stars.ts). Every
      // derivative is taken here, at the top of the lens's own function (no branch holds one).
      const dS = normalize(R.mul(dir)).toVar();
      const f0 = clamp(length(fwidth(view)).mul(uFloorScale), LENS_F_MIN, LENS_F_MAX).toVar();
      const f = clamp(length(fwidth(dS)), LENS_F_MIN, LENS_F_MAX).toVar();
      const stars = buildSpaceStarField(dS, assets.stars!, { ...plain, floor0: f0.mul(LENS_FLOOR_SHARE) });
      const lutU = (x: N): N => vec2(log(x.div(LENS_F_MIN)).mul((LENS_LUT_N - 1) / (LENS_LUT_N * Math.log(LENS_F_MAX / LENS_F_MIN))).add(0.5 / LENS_LUT_N), 0.5);
      const a0 = (tslTexture(lutTex, lutU(f0)) as N).level(0), a1 = (tslTexture(lutTex, lutU(f)) as N).level(0);
      const uvv = vec2(atan(dS.z, dS.x).mul(1 / (2 * Math.PI)).add(0.5), asin(clamp(dS.y, -1, 1)).mul(1 / Math.PI).add(0.5));
      const dens = (tslTexture(densTex, uvv) as N).level(0).rgb;
      return vec3(stars).add(dens.mul(a0.x.mul(max(a1.y.sub(a0.y), float(0)))));
    } : undefined,
    pointsMean: state.mean,
    sources: bodies.length ? (ray: BlackHoleRay): { behind: N; front: N } => {
      let behind: N = vec3(0), front: N = vec4(0, 0, 0, 0);
      for (const b of bodies) {
        const s = starLensSource(b, ray);
        behind = behind.add(s.behind);
        // two bodies in front (a pair nearer than the hole): the nearer is not known here — their light adds, the opacity is the larger
        front = vec4(front.xyz.add(s.front.xyz), max(front.w, s.front.w));
      }
      return { behind, front };
    } : undefined,
  };
  return {
    sky, setSystem, state, bodies, stars: tables ? 'conserve' : 'legacy', tables,
    wideMean: (fov, h) => (tables ? lensWideMean(tables, centreFootprint(fov, h)) : state.mean),
    setFloorScale: (k) => { (uFloorScale as unknown as { value: number }).value = Number.isFinite(k) && k > 0 ? k : 1; },
    dispose(): void { lutTex?.dispose(); densTex?.dispose(); },
  };
}

/** What SpaceModule lends the controller (closures over the sector — no module import here). */
export interface BlackHoleEnv {
  /** The sector root (render space: the root sits at the scene origin). */
  parent(): THREE.Object3D | null;
  scene(): THREE.Scene | null;
  renderer(): unknown;
  /** The frame's camera: the camera object, its render-space position and world orientation (rig × camera). */
  camera(): { cam: THREE.PerspectiveCamera; pos: THREE.Vector3; quat: THREE.Quaternion } | null;
  /** The floating origin (absolute metres of the render origin). */
  origin(): V3;
  heightPx(): number;
  /** The flag under its kill switch — read every frame (LIVE: off / the VR profile hide it). */
  flag(): BlackHoleQualityFlag;
  assets(): Promise<SpaceSkyAssets>;
  systemId(): string;
  /** Install a background node (null = the plain space background again). */
  setBackground(node: unknown | null): void;
  /** The star bodies the sector is drawing (space_sun.sunBodies) — the lens bends them. Absent = none (a bed with no sun). */
  stars?(): StarBody[];
  /**
   * Is the lensed background really the sky the player sees? False under the LEGACY sky (a void shell covers the
   * background pass): there the lens is only its cap, and a star handed to it would vanish outside the cap — the quads keep it.
   */
  skyIsLensed?(): boolean;
}

/** One star as the lens holds it this frame. */
export interface BlackHoleStarState {
  name: string; owned: boolean; front: boolean; betaDeg: number; thetaEDeg: number; imageDeg: number; glare: number; distM: number | null;
}
export interface BlackHoleControllerState {
  placed: boolean; shown: boolean; flag: BlackHoleQualityFlag; mass: number; rsM: number;
  abs: V3 | null; render: V3 | null; disc: ResolvedDisc | null; lens: Record<string, unknown> | null; warm: boolean; error: string;
  /** The stars the lens draws (empty: none handed over — the quads draw them). */
  stars: BlackHoleStarState[];
}

/** One hole per sector entry; the lever and a future destination both call `place`. */
export class BlackHoleController {
  private lens: BlackHoleLens | null = null;
  private skyKit: ReturnType<typeof spaceLensSky> | null = null;
  private abs: V3 | null = null;
  private mass = BLACK_HOLE_DEFAULT_MASS;
  private disc: ResolvedDisc | null = null;
  private shown = false;
  private warm = false;
  private error = '';
  private gen = 0;
  private readonly _v = new THREE.Vector3();
  private assets: SpaceSkyAssets | null = null;
  /** The bodies compiled into the live lens's shader, and those this controller currently owns. */
  private bodies: StarBody[] = [];
  private owned: StarBody[] = [];
  private starStates: BlackHoleStarState[] = [];
  private readonly _right = new THREE.Vector3();
  private readonly _up = new THREE.Vector3();

  constructor(private readonly env: BlackHoleEnv) {}

  /** Place (or move) the hole. `at` absolute metres; default: 50 r_s ahead of the camera. */
  async place(input: BlackHolePlaceInput = {}): Promise<BlackHoleControllerState> {
    const gen = ++this.gen;
    this.error = '';
    try {
      const assets = await this.env.assets();
      if (gen !== this.gen) return this.state();
      this.mass = typeof input.mass === 'number' && Number.isFinite(input.mass) ? input.mass : BLACK_HOLE_DEFAULT_MASS;
      const rs = schwarzschildRadiusM(this.mass);
      this.disc = resolveDisc(input.disc);
      const at = normalizeAt(input.at);
      if (at) this.abs = at;
      else {
        const c = this.env.camera();
        if (!c) throw new Error('no camera');
        const o = this.env.origin();
        const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(c.quat);
        this.abs = placementAhead({ x: c.pos.x + o.x, y: c.pos.y + o.y, z: c.pos.z + o.z }, fwd, rs);
      }
      this.assets = assets;
      const bodies = this.wantedBodies();
      const fresh = !this.skyKit || !sameBodies(this.skyKit.bodies, bodies);
      if (fresh) { disposeAfterSwap(this.skyKit); this.skyKit = spaceLensSky(assets, bodies); this.bodies = bodies; }
      this.skyKit!.setSystem(this.env.systemId());
      const q = this.env.flag();
      this.build(q === 'off' ? 'high' : q, fresh);
      this.lens!.set({ rs, disc: this.disc.on, discNormal: new THREE.Vector3(this.disc.normal.x, this.disc.normal.y, this.disc.normal.z), discOuter: this.disc.outer, temperature: this.disc.temperature, luminosity: this.disc.luminosity, beaming: this.disc.beaming, spin: this.disc.spin, gasContrast: this.disc.gas });
      this.frame(0);
      await this.warmBody(gen);
    } catch (e) {
      this.error = String((e as Error)?.message ?? e);
    }
    return this.state();
  }

  /** The star bodies the lens should draw now: the sector's, when the lensed background is really the sky. */
  /** A/B on a real GPU: `localStorage atlas:bh-sun = 'rig'` keeps the sun on its quads while a hole is shown — the picture before 2026-10-08 (the cap cuts it). */
  private readonly sunAsSource: boolean = (() => { try { return localStorage.getItem('atlas:bh-sun') !== 'rig'; } catch { return true; } })();
  private wantedBodies(): StarBody[] {
    return !this.sunAsSource || this.env.skyIsLensed?.() === false ? [] : (this.env.stars?.() ?? []);
  }

  /** Give every owned star back to its quads (the lens is hidden, removed, or being rebuilt). */
  private releaseStars(): void {
    for (const b of this.owned) setBodyLensOwned(b, false);
    this.owned = []; this.starStates = [];
  }

  /**
   * The stars for this frame: where each stands relative to the hole (bent beyond it, un-bent in front), the lens
   * source's uniforms, the quads' ownership and the glare's seat. `camToHole` in render space (doubles).
   */
  private seatStars(lens: BlackHoleLens, c: { pos: THREE.Vector3; quat: THREE.Quaternion }, camToHole: V3, rs: number): void {
    const right = this._right.set(1, 0, 0).applyQuaternion(c.quat), up = this._up.set(0, 1, 0).applyQuaternion(c.quat);
    const exact = (u: number, beta: number, distRs: number): number | null => bhImageAngle(lens.tables, u, beta, distRs);
    const states: BlackHoleStarState[] = [];
    const deg = (v: number): number => Math.round((v * 180 / Math.PI) * 1000) / 1000;
    for (const b of this.bodies) {
      const live = !!b.group.parent && this.shown;
      let seat: StarLensSeat | null = null;
      if (live) {
        seat = starLensSeat(camToHole, rs, b.dirNow, b.distNow, b.angR, exact);
        writeBodyLens(b, seat, right, up, true);
        seatBodyGlare(b, seat.imageDir, seat.glare);
      }
      setBodyLensOwned(b, live);
      if (live && !this.owned.includes(b)) this.owned.push(b);
      states.push({
        name: b.group.name, owned: live, front: seat?.front ?? false, betaDeg: seat ? deg(seat.beta) : 0, thetaEDeg: seat ? deg(seat.thetaE) : 0,
        imageDeg: seat ? deg(seat.imageAngle) : 0, glare: seat ? Math.round(seat.glare * 1000) / 1000 : 1, distM: Number.isFinite(b.distNow) ? b.distNow : null,
      });
    }
    this.owned = this.owned.filter((b) => b.lensOwned);
    this.starStates = states;
  }

  /** Build (or rebuild at another table size, or — `force` — for another set of star bodies) the lens and seat its body under the sector root. */
  private build(q: 'low' | 'high', force = false): void {
    if (this.lens && this.lens.quality === q && !force) return;
    const had = this.lens;
    this.lens = new BlackHoleLens({ quality: q, sky: this.skyKit!.sky });
    if (had) {
      const d = this.disc!;
      this.lens.set({ rs: schwarzschildRadiusM(this.mass), disc: d.on, discNormal: new THREE.Vector3(d.normal.x, d.normal.y, d.normal.z), discOuter: d.outer, temperature: d.temperature, luminosity: d.luminosity, beaming: d.beaming, spin: d.spin, gasContrast: d.gas });
      had.body.removeFromParent(); disposeAfterSwap(had); // its body leaves NOW; its tables are still the background of the frames in flight (they were freed in this tick)
      if (this.shown) this.env.setBackground(null);       // the plain sky until the new lens is shown — never a background whose lens is being freed
      this.shown = false; // the new lens's background goes in at the next frame
      this.releaseStars(); // …and the quads draw the stars until then
    }
    this.lens.body.visible = false;
    this.env.parent()?.add(this.lens.body);
  }

  /** THE WARM: compile the body's pipeline before its first draw (a private scene, the main scene's lights). */
  private async warmBody(gen: number): Promise<void> {
    const r = this.env.renderer() as { compileAsync?(s: THREE.Object3D, c: THREE.Camera, t?: THREE.Scene | null): Promise<void> } | null;
    const c = this.env.camera(), lens = this.lens;
    if (!r?.compileAsync || !c || !lens) { this.warm = true; return; }
    const parent = lens.body.parent;
    const tmp = new THREE.Scene();
    tmp.add(lens.body);
    lens.body.visible = true;
    try { await r.compileAsync(tmp, c.cam, this.env.scene()); } catch { /* the first draw compiles it instead */ }
    tmp.remove(lens.body);
    if (gen === this.gen && this.lens === lens) { (parent ?? this.env.parent())?.add(lens.body); lens.body.visible = this.shown; }
    this.warm = true;
  }

  /** Per frame (after the sun and the camera): the flag, the FP64 → render position, the lens uniforms, the swap. */
  frame(dt: number): void {
    const lens = this.lens;
    if (!lens || !this.abs) return;
    const q = this.env.flag();
    if (q === 'off') {
      if (this.shown) { lens.body.visible = false; this.env.setBackground(null); this.shown = false; }
      this.releaseStars();
      return;
    }
    // a star body that arrived or left since the lens was built (a pair's second star, the sky going legacy) is another shader
    const wanted = this.wantedBodies();
    if (this.assets && this.skyKit && !sameBodies(this.skyKit.bodies, wanted)) {
      disposeAfterSwap(this.skyKit); // its two small tables are still sampled by the frames in flight
      this.skyKit = spaceLensSky(this.assets, wanted); this.bodies = wanted;
      this.skyKit.setSystem(this.env.systemId());
      this.warm = false;
      const gen = this.gen;
      this.build(q, true);
      void this.warmBody(gen);
      return;
    }
    if (q !== lens.quality) { this.build(q); this.frame(dt); return; }
    const c = this.env.camera();
    if (!c) return;
    const rp = renderPosition(this.abs, this.env.origin());
    // THE THREE SEATS: while a far body sits behind it the cap stands forward (black_hole.ts); else at the engine's own seat
    this.capForwardNow = this.capForward && this.backSeatOn;
    lens.set({ position: this._v.set(rp.x, rp.y, rp.z), depthCodes: this.capForwardNow ? CAP_FORWARD_CODES : BH_BODY_DEPTH_CODES });
    lens.update(c.cam, dt, this.env.heightPx(), c.pos);
    // the wide-beam mean follows the DISPLAY (the plain sky's own mean at this pass's pixel) — lens_stars
    if (this.skyKit?.tables) lens.setPointsMean(this.skyKit.wideMean(((c.cam as THREE.PerspectiveCamera).fov * Math.PI) / 180, this.env.heightPx()));
    if (!this.shown && this.warm) {
      lens.body.visible = true;
      this.env.setBackground(lens.backgroundNode());
      this.shown = true;
    }
    this.seatStars(lens, c, { x: rp.x - c.pos.x, y: rp.y - c.pos.y, z: rp.z - c.pos.z }, schwarzschildRadiusM(this.mass));
  }

  /** Remove the hole: the body leaves, the plain background returns. */
  remove(): void {
    this.gen++;
    // the body and the background leave NOW; the lens's geometry, material and tables are freed after the window — at a sector teardown they were freed under frames still in flight (the deferred-teardown law)
    if (this.lens) { if (this.shown) this.env.setBackground(null); this.lens.body.removeFromParent(); disposeAfterSwap(this.lens); }
    if (this.skyKit) { disposeAfterSwap(this.skyKit); this.skyKit = null; }
    this.lens = null; this.abs = null; this.shown = false; this.warm = false;
    this.capForward = false; this.capForwardNow = false;
    this.releaseStars(); // the quads draw the stars again, from this frame
  }
  dispose(): void { this.remove(); }

  private readonly _cap = { x: 0, y: 0, z: 0, coreRad: 0, backSeatM: 0, frontSeatM: FRONT_SEAT_M, forward: false };
  private capForward = false;    // asked for by the sector's anchors (a body is seated behind the cap)
  private capForwardNow = false; // what this frame's lens was given
  /** The sector's anchors say whether any far body sits behind the cap; the cap takes its forward seat at the next frame. */
  setCapForward(on: boolean): void { this.capForward = on; }
  private backSeatOn = ((): boolean => { try { return localStorage.getItem(BACK_SEAT_KEY) !== 'off'; } catch { return true; } })();
  /** The A/B, live (the lever and the probes): false leaves every far body at the usual anchor, as it was. */
  setBackSeat(on: boolean): void { this.backSeatOn = on; }
  /**
   * THE BACK SEAT's inputs for the sector's far-body anchors: the DRAWN hole (absolute metres), its cap's angular radius
   * and how far from the camera a body beyond it must stand to be behind the cap (black_hole.ts). null = no hole is
   * drawn (or the A/B switch is off). The returned object is reused.
   */
  capFrame(): { x: number; y: number; z: number; coreRad: number; backSeatM: number; frontSeatM: number; forward: boolean } | null {
    if (!this.backSeatOn || !this.shown || !this.lens || !this.abs) return null;
    const c = this.env.camera();
    const st = this.lens.state();
    if (!c || !(st.coreRad > 0)) return null;
    const cam = c.cam as THREE.PerspectiveCamera;
    const reversed = (cam as unknown as { reversedDepth?: boolean }).reversedDepth === true;
    const o = this._cap;
    o.x = this.abs.x; o.y = this.abs.y; o.z = this.abs.z; o.coreRad = st.coreRad;
    // reversed depth seats the cap at ≤ 0.45 of the far plane and has codes to spare behind it (it never moves forward)
    o.backSeatM = reversed ? cam.far * 0.47 : bhDepthSafeDistance(cam.near, cam.far, BACK_SEAT_CODES);
    o.forward = this.capForwardNow && !reversed;
    return o;
  }

  state(): BlackHoleControllerState {
    const o = this.env.origin();
    return {
      placed: !!this.lens && !!this.abs, shown: this.shown, flag: this.env.flag(), mass: this.mass, rsM: schwarzschildRadiusM(this.mass),
      abs: this.abs, render: this.abs ? renderPosition(this.abs, o) : null, disc: this.disc,
      lens: this.lens ? { ...this.lens.state(), starsP99: this.skyKit?.state.p99 ?? 0, starsMean: this.skyKit?.state.mean ?? 0 } : null,
      warm: this.warm, error: this.error, stars: this.starStates,
    };
  }
}

/** The same star bodies, in the same order (a lens's shader is compiled for one set). */
function sameBodies(a: readonly StarBody[], b: readonly StarBody[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
